import { useMemo } from 'react';
import { portalApiUrl } from '../../portal/portalClient';
import { FEATURE_INSTANCE_NAME, isFeatureEnabled } from '../../utils/features';
import { useAuth } from '../useAuth';

/**
 * The three things every portal query needs, plus the single guard they all share.
 *
 * `canQuery` is deliberately one value: three separate `enabled` conditions repeated
 * across four hooks is how one of them ends up missing the token check and firing an
 * unauthenticated request.
 *
 * `isPortalSource` is the shared discriminator for "is the portal actually the source of
 * truth" — the flag alone is not enough. A configured host is just as necessary as the
 * flag: flag-on with no host (local dev, an on-demand env, or `${CUSTOMER_PORTAL_API_URL}`
 * left unsubstituted at deploy) must degrade to the guardian list exactly like flag-off,
 * not sit permanently disabled waiting on a request that can never fire.
 */
export const usePortalAccess = () => {
  const { accessToken } = useAuth();
  const isEnabled = isFeatureEnabled(FEATURE_INSTANCE_NAME);
  const baseUrl = portalApiUrl();
  const token = accessToken ?? '';

  return useMemo(
    () => ({
      isEnabled,
      baseUrl,
      token,
      isPortalSource: isEnabled && !!baseUrl,
      canQuery: isEnabled && !!baseUrl && !!token,
    }),
    [isEnabled, baseUrl, token],
  );
};
